import { Component, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { PortalTeamsService } from './portal-teams.service';
import { PortalTeamsModel, PortalTeamRow } from './portal-teams.models';
import {
  toCsvRow,
  triggerCsvDownload
} from 'src/app/teams/team-forms-evaluation-results/team-forms-evaluation-results.utils';

@Component({
  selector: 'app-portal-teams',
  templateUrl: './portal-teams.component.html',
  styleUrls: ['./portal-teams.component.scss'],
  standalone: false,
  providers: [DatePipe]
})
export class PortalTeamsComponent implements OnInit {
  model: PortalTeamsModel = { columns: [], rows: [] };
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<PortalTeamRow>([]);
  loaded = false;
  loadError = false;

  // The table is behind an @if, so wire up sorting once the directive appears.
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) this.dataSource.sort = sort;
  }

  constructor(
    private teamsService: PortalTeamsService,
    private appConfigService: AppConfigService,
    private router: Router,
    private datePipe: DatePipe
  ) {
    // Sort on the raw value (ISO dates sort chronologically, numbers numerically).
    this.dataSource.sortingDataAccessor = (row, columnId) => row.values[columnId] ?? '';
  }

  // Display ISO date values (yyyy-MM-dd, what the server sends for date columns) as "Apr 20, 2026".
  // Formatted in UTC so the date-only value isn't shifted by the local timezone.
  formatCell(value: string): string {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return this.datePipe.transform(value, 'MMM d, y', 'UTC') ?? value;
    }
    return value;
  }

  get tabLabel(): string {
    return (
      this.appConfigService.tenant?.portalConfiguration?.teamsConfiguration?.teamsTabLabel ??
      'Teams'
    );
  }

  async ngOnInit(): Promise<void> {
    const teamsConfig = this.appConfigService.tenant?.portalConfiguration?.teamsConfiguration;
    if (teamsConfig?.showTeamsTab !== true) {
      this.router.navigate(['portal/not-found'], { skipLocationChange: true });
      return;
    }

    try {
      this.model = await this.teamsService.getMyTeams();
      this.displayedColumns = this.model.columns.map((c) => c.key);
      this.buildDisplayValues();
      this.dataSource.data = this.model.rows;
    } catch {
      this.loadError = true;
    } finally {
      // Always leave the loading state so the page renders (table, empty, or error).
      this.loaded = true;
    }
  }

  // Pre-format each cell once after fetch so the template isn't running formatCell on every
  // change-detection cycle. Raw `values` are kept for sorting.
  private buildDisplayValues(): void {
    for (const row of this.model.rows) {
      const display: Record<string, string> = {};
      for (const col of this.model.columns) {
        display[col.key] = this.formatCell(row.values[col.key]);
      }
      row.displayValues = display;
    }
  }

  exportCsv(): void {
    // Export in the order currently displayed (respects the active sort).
    const rows = this.dataSource.sortData(this.dataSource.data, this.dataSource.sort);
    const csv = [
      toCsvRow(this.model.columns.map((c) => c.displayName)),
      ...rows.map((row) =>
        toCsvRow(this.model.columns.map((c) => row.displayValues?.[c.key] ?? ''))
      )
    ].join('\r\n');

    const base = (this.tabLabel || 'Teams').replace(/[^A-Za-z0-9-_]+/g, '_').replace(/_+/g, '_');
    triggerCsvDownload(csv, `${base}.csv`);
  }
}
